import { isAbortError, Point2D } from '@trimble/utilities';

const HALF = 2;
const METADATA_FILE = 'metadata.json';
const CLOUD_JS_FILE = 'cloud.js';

type FetchLike = (
  input: string,
  init?: { signal?: AbortSignal }
) => Promise<Pick<Response, 'ok' | 'json'>>;

/**
 * World XY center of a Potree tileset, from metadata.json (v2) or cloud.js (v1).
 * Used when no 3D pane is present to load a model bounding box.
 */
export async function fetchPotreeBoundingCenter(
  tilesetUrl: string,
  signal: AbortSignal,
  fetchImpl: FetchLike = fetch
): Promise<Point2D | undefined> {
  for (const url of tilesetMetadataUrls(tilesetUrl)) {
    const center = await fetchCenterFromUrl(url, signal, fetchImpl);
    if (center !== undefined) {
      return center;
    }
  }
  return undefined;
}

export function tilesetMetadataUrls(tilesetUrl: string): readonly string[] {
  const queryIndex = tilesetUrl.indexOf('?');
  const pathWithSlash =
    queryIndex >= 0 ? tilesetUrl.slice(0, queryIndex) : tilesetUrl;
  const query = queryIndex >= 0 ? tilesetUrl.slice(queryIndex) : '';
  let path = pathWithSlash;
  while (path.endsWith('/')) {
    path = path.slice(0, -1);
  }
  if (path.endsWith(METADATA_FILE) || path.endsWith(CLOUD_JS_FILE)) {
    return [tilesetUrl];
  }
  return [
    `${path}/${METADATA_FILE}${query}`,
    `${path}/${CLOUD_JS_FILE}${query}`,
  ];
}

async function fetchCenterFromUrl(
  url: string,
  signal: AbortSignal,
  fetchImpl: FetchLike
): Promise<Point2D | undefined> {
  try {
    const response = await fetchImpl(url, { signal });
    if (!response.ok) {
      return undefined;
    }
    const body: unknown = await response.json();
    return parsePotreeBoundingCenter(body);
  } catch (error) {
    // A missing metadata.json is expected (the caller then tries cloud.js), but
    // an abort must reach the load handler so it can skip the geo-reference write.
    if (signal.aborted || isAbortError(error)) {
      throw error;
    }
    return undefined;
  }
}

export function parsePotreeBoundingCenter(body: unknown): Point2D | undefined {
  if (body === null || typeof body !== 'object') {
    return undefined;
  }

  const record = body as Record<string, unknown>;
  const box = parseBoundingBox(record['boundingBox']);
  if (box === undefined) {
    return undefined;
  }

  const offset = parseXyz(record['offset']);
  const minX = box.minX + (offset?.x ?? 0);
  const minY = box.minY + (offset?.y ?? 0);
  const maxX = box.maxX + (offset?.x ?? 0);
  const maxY = box.maxY + (offset?.y ?? 0);

  return {
    x: (minX + maxX) / HALF,
    y: (minY + maxY) / HALF,
  };
}

function parseBoundingBox(
  raw: unknown
): { minX: number; minY: number; maxX: number; maxY: number } | undefined {
  if (raw === null || typeof raw !== 'object') {
    return undefined;
  }

  const record = raw as Record<string, unknown>;
  const min = parseXyz(record['min']);
  const max = parseXyz(record['max']);
  if (min !== undefined && max !== undefined) {
    return { minX: min.x, minY: min.y, maxX: max.x, maxY: max.y };
  }

  const lx = toFiniteNumber(record['lx']);
  const ly = toFiniteNumber(record['ly']);
  const ux = toFiniteNumber(record['ux']);
  const uy = toFiniteNumber(record['uy']);
  if (
    lx !== undefined &&
    ly !== undefined &&
    ux !== undefined &&
    uy !== undefined
  ) {
    return { minX: lx, minY: ly, maxX: ux, maxY: uy };
  }

  return undefined;
}

function parseXyz(raw: unknown): Point2D | undefined {
  if (Array.isArray(raw) && raw.length >= HALF) {
    const x = toFiniteNumber(raw[0]);
    const y = toFiniteNumber(raw[1]);
    if (x !== undefined && y !== undefined) {
      return { x, y };
    }
    return undefined;
  }

  if (raw === null || typeof raw !== 'object') {
    return undefined;
  }

  const record = raw as Record<string, unknown>;
  const x = toFiniteNumber(record['x']);
  const y = toFiniteNumber(record['y']);
  if (x !== undefined && y !== undefined) {
    return { x, y };
  }
  return undefined;
}

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return value;
}
